// OperationalHealth.jsx
import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useAccount } from "./account-context";
import { getApiPaths } from "./constants/healthEndpoints";
import useApi from "./useApi";
import DisplayLineChart from "./DisplayLineChart";
import OperationalInsights from "./OperationalInsights";

import { navigation, platform, nerdlet } from "nr1";
import StoresDataFetcher from "./StoresDataFetcher";
import StoreStatusDashboard from "./StoreStatusDashboard";
import {
  labelToServiceKey,
  openEntityWithOneDayDuration,
  getChicagoTimeDifferenceInMinutes,
  buildAllServicesDisplayData,
  buildAlertsFromAllServices,
  getRelativeTime
} from "./buildServiceItems";
import { SERVICE_MAIN_SERVICES } from "./cardsConfig";
//import StoreStatusDetails from "./StoreStatusDetails";
import Collapsible from "./Collapsible";

import OperationalServiceCard from "./OperationalServiceCard";
import OperationalProgressBar from "./OperationalProgressBar";
import OperationalServiceCardCollapse from "./OperationalServiceCardCollapse";
import { time } from "echarts";
import AlertsAccordion from "./AlertsAccordion";
import OperationalRightPanelCard from "./OperationalRightPanelCard";
/* ------------ Constants & Helpers ------------- */
const ENTITY_GUIDS = {
  DigitalOrdering: "MzU5MjU1NnxWSVp8REFTSEJPQVJEfGRhOjEyMjY0OTYw",
  Sesame:          "MzU5MjU1NnxWSVp8REFTSEJPQVJEfGRhOjEyMjEyOTc2",
  eProd:           "", // TODO: replace with the real GUID
  MyStore:         "MzU5MjU1NnxWSVp8REFTSEJPQVJEfGRhOjEyMjEyOTc5",
};
const LIMITS = {
  RED_MAX: 80,
  AMBER_MAX: 99,
};

const TABS = {
  HEALTH: "health",
  AI: "ai",
};

const UI = {
  TITLE: "Service Health",
  FILTER_ON_SERVICE: "Unhealthy",
  FILTER_DEFAULT: "All",
};

function toCards(dataObj) {
  // servicesData can be either an object or [object]
  if (!dataObj || typeof dataObj !== "object") return [];
  return Object.entries(dataObj)
    .filter(
      ([key, value]) =>
        !["timestamp", "x", "y"].includes(key) && typeof value === "number",
    )
    .map(([key, value], idx) => ({
      id: idx + 1,
      label: key,
      percentage: Number(value) || 0,
    }));
}

function isImpactedColor(color) {
  if (!color) return false;
  const c = String(color).toLowerCase();
  return c === "red" || c === "amber" || c === "#ffc107";
}

function getGaugeColor(val) {
  const v = Math.max(0, Math.min(100, Number(val) || 0));
  if (v < LIMITS.RED_MAX) return "oh-red";
  if (v <= LIMITS.AMBER_MAX) return "oh-yellow";
  return "oh-green";
}

const SERVICE_TO_ISSUE_MAP = {
  "Digital Ordering": "DigitalOrdering",
  "My Store": "MyStore",
  Sesame: "Sesame",
  ePROD: "eProd",
};

function resolveIssueForService(serviceLabel) {
  return SERVICE_TO_ISSUE_MAP[serviceLabel] ?? serviceLabel;
}
function getServiceIssues(storesData, serviceName) {
  if (!Array.isArray(storesData) || !serviceName) return [];

  return storesData.filter((row) => {
    return (
      (row?.color === "red" || row?.color === "amber") &&
      (row?.status === "down"  || row?.status === "degraded") 
      && String(row?.issue ?? "").trim() === serviceName
    );
  });
}
/**
 * Groups only red + down stores by "Issue - Detail".
 * Output shape:
 * {
 *   "Sesame - Zombie Plugins": {
 *      issue: "Sesame",
 *      detail: "Zombie Plugins",
 *      count: 3,
 *      stores: [{store, issue, detail}]
 *   }
 * }
 */
function getIssueWiseDownStores(storesData) {
  if (!Array.isArray(storesData) || storesData.length === 0) return {};

  const output = {};

  for (const row of storesData) {
    if (row?.color === "red" && row?.status === "down" && row?.issue) {
      const issueName = String(row.issue).trim();
      const detailName = String(row.detail ?? "Unknown Detail").trim();
      const groupKey = `${issueName} - ${detailName}`;

      if (!output[groupKey]) {
        output[groupKey] = {
          issue: issueName,
          detail: detailName,
          count: 0,
          stores: [],
        };
      }
      output[groupKey].count += 1;
      output[groupKey].stores.push({
        store: row.store,
        issue: issueName,
        detail: detailName,
      });
    }
  }

  return output;
}
export function getDetailGroups(input = []) {
  const data = Array.isArray(input) ? input : [];  // <- critical guard
  return data
    
    .filter(item => item?.alert && String(item.alert).trim() !== '')
    .reduce((acc, item) => {
      const key = item.alert || "(no detail)";

      if (!acc[key]) {
        acc[key] = { alert: key, count: 0, stores: [] };
      }
      //console.log("Grouping item under key:", key, { item });
      acc[key].count++;
      acc[key].stores.push(item);

      return acc;
    }, {});
}
/* ------------ Component ------------- */

export default function OperationalHealth() {
  // Core dashboards state
  const [stores, setStores] = useState([]);
  const [isTrendOpen, setIsTrendOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.HEALTH);

  const [activeAlertTab, setActiveAlertTab] = useState("right-stores"); // "right-stores" | "right-alerts"

  const [loading, setLoading] = useState(false);

  const [modalUrl, setModalUrl] = useState(null);
  const openModal = (url) => setModalUrl(url);
  const closeModal = () => setModalUrl(null);

  const [showHexagonDemo, setShowHexagonDemo] = useState(false);
  const [storeCard, setStoreCard] = useState(false); // true => open StoreStatusDashboard with source="stores"

  const [selectedService, setSelectedService] = useState(null);
  const [filter, setFilter] = useState(UI.FILTER_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStore, setSelectedStore] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
   const [relativeTime, setRelativeTime] = useState(null);
  // API setup
  const { accountId, userEmail, language, setLanguage } = useAccount();

  useEffect(() => {
    setIsTrendOpen(false);
  }, [accountId]);

  const { STORES_VIEW, SERVICES_VIEW } = getApiPaths(accountId);
  const [pageByFilter, setPageByFilter] = useState({
    All: 1,
    Healthy: 1,
    Unhealthy: 1,
    Amber: 1,
  });
  // const {
  //   data: storesData = [],
  //   loading: storesLoading,
  //   error: storesError,
  // } = useApi(STORES_VIEW, [accountId]);

  // const {
  //   data: servicesDataRaw = {},
  //   loading: servicesLoading,
  //   error: servicesError,
  // } = useApi(SERVICES_VIEW, [accountId]);

  // // Normalize servicesData (API might return [ {..} ] or {..})
  // const servicesData = useMemo(() => {
  //   if (Array.isArray(servicesDataRaw)) {
  //     return servicesDataRaw[0] || {};
  //   }
  //   return servicesDataRaw || {};
  // }, [servicesDataRaw]);

  // // Build service cards (label + percentage)
  // const cards = useMemo(() => toCards(servicesData), [servicesData]);

  // // Issue grouping per current storesData
  // const issueGroups = useMemo(
  //   () => getIssueWiseDownStores(storesData),
  //   [storesData],
  // );
  const {
  data: storesData = [],
  loading: storesLoading,
  error: storesError,
  refetch: refetchStores,
} = useApi(STORES_VIEW);

const {
  data: servicesDataRaw = {},
  loading: servicesLoading,
  error: servicesError,
  refetch: refetchServices,
} = useApi(SERVICES_VIEW);


const WS_URL =
  "wss://mv38msr7c2.execute-api.us-east-1.amazonaws.com/DEV";

const heartbeatRef = useRef(null);
const reconnectRef = useRef(null);
const socketRef = useRef(null);

// useEffect(() => {
//   let shouldReconnect = true;

//   const connectSocket = () => {
//     console.log("Connecting WebSocket...");

//     const socket = new WebSocket(WS_URL);
//     socketRef.current = socket;

//     socket.onopen = () => {
//       console.log("WebSocket connected");

//       //  Keep socket alive
//       heartbeatRef.current = setInterval(() => {
//         if (socket.readyState === WebSocket.OPEN) {
//           socket.send(JSON.stringify({ action: "notify" }));
//         }
//       }, 30000); // every 30 seconds
//     };

//     socket.onmessage = (event) => {
//       const message = JSON.parse(event.data);
//       console.log("WebSocket message received:", message);

//       if (message.type === "REFRESH") {
//         refetchStores();
//         refetchServices();
//       }
//     };

//     socket.onerror = (err) => {
//       console.error("WebSocket error", err);
//     };

//     socket.onclose = () => {
//       console.log("WebSocket closed");

//       clearInterval(heartbeatRef.current);
//       heartbeatRef.current = null;
//       socketRef.current = null;

//       if (shouldReconnect) {
//         clearTimeout(reconnectRef.current);
//         reconnectRef.current = setTimeout(connectSocket, 3000);
//       }
//     };
//   };

//   //  connect once
//   connectSocket();

//   return () => {
//     shouldReconnect = false;
//     clearInterval(heartbeatRef.current);
//     clearTimeout(reconnectRef.current);

//     if (socketRef.current) {
//       socketRef.current.close();
//     }
//   };
// }, []); //  no deps



useEffect(() => {
  //console.log("Starting 5-minute auto refresh");

  const intervalId = setInterval(() => {
    console.log("Auto refresh (5 minutes)");
    refetchStores();
    refetchServices();
  }, 5 * 60 * 1000);

  return () => clearInterval(intervalId);
}, []);




  const servicesDataDisplay = useMemo(() => {
    if (!storesData || !servicesDataRaw) return {};
    return buildAllServicesDisplayData(storesData, servicesDataRaw);
  }, [storesData, servicesDataRaw]);

  const alertsDataDisplay = useMemo(() => {
    return buildAlertsFromAllServices(servicesDataDisplay);
  }, [servicesDataDisplay]);
  //console.log("Services Data Display:", servicesDataDisplay);
  //console.log("Alerts Data Display:", alertsDataDisplay);

  // Impact metrics
  // const impactedStoresCount = useMemo(() => {
  //   return (storesData || []).reduce(
  //     (acc, s) => acc + (isImpactedColor(s?.color) ? 1 : 0),
  //     0,
  //   );
  // }, [storesData]);
  const impactedStoresCount = useMemo(() => {
  return (storesData || []).reduce((count, store) => {
    const isImpacted =
      store?.status === "down" && store?.color === "red";

    return count + (isImpacted ? 1 : 0);
  }, 0);
}, [storesData]);

  const allStoresCount = useMemo(() => {
    const ids = new Set();
    for (const s of storesData || []) {
      if (s?.store != null) ids.add(String(s.store));
    }
    for (const s of stores || []) {
      if (s?.store != null) ids.add(String(s.store));
    }
    return ids.size;
  }, [storesData, stores]);

  const impactedPercent = allStoresCount
    ? Math.round((impactedStoresCount / allStoresCount) * 100)
    : 0;

  const healthPercent = allStoresCount
    ? Math.round(100 - (impactedStoresCount / allStoresCount) * 100)
    : 0;

  /* ------------ Handlers ------------- */
const lastUpdatedTimestamp = useMemo(() => {
  if (!storesData?.length) return null;
  return Math.max(...storesData.map(i => i.timestamp));
}, [storesData]);


useEffect(() => {
  if (!lastUpdatedTimestamp) return;

  const update = () => {
    setRelativeTime(getRelativeTime(lastUpdatedTimestamp));
  };

  // Set immediately
  update();

  // Update every minute (UX best practice)
  const interval = setInterval(update, 60_000);

  return () => clearInterval(interval);
}, [lastUpdatedTimestamp]);

//console.log("Last Updated Timestamp:", lastUpdatedTimestamp, "Relative Time:", relativeTime);
  const handleCardClick = (card) => {
    //console.log("Card clicked:", card);
    const serviceKey = labelToServiceKey(card.name);
    const svc = SERVICE_MAIN_SERVICES.find((s) => s.key === serviceKey);
    if (!svc) {
      // eslint-disable-next-line no-console
      console.warn("Service not found:", serviceKey);
      return;
    }
    // Open service dashboard
    setSelectedService(svc);
    setSelectedStore(null);
    setFilter(UI.FILTER_ON_SERVICE);
    setCurrentPage(1);
    setStores([]);
    setStoreCard(false);
    setShowHexagonDemo(true);
  };

  const handleStoreCardClick = () => {
    // Open store dashboard
    setStoreCard(true);
    setSelectedService(null);
    setSelectedStore(null);
    setCurrentPage(1);
    setStores([]);
    setFilter("Unhealthy");
    setShowHexagonDemo(true);
  };

  const handleBackFromHexagonDemo = useCallback(() => {
    setShowHexagonDemo(false);
    setStoreCard(false);
    setSelectedService(null);
    setSelectedStore(null);
    setFilter(UI.FILTER_DEFAULT);
    setCurrentPage(1);
    setStores([]);
  }, []);

  const onGoHomeFromDash = useCallback(() => {
    // from inside StoreStatusDashboard
    handleBackFromHexagonDemo();
  }, [handleBackFromHexagonDemo]);

  const onStoreClick = () => {
    // reserved for future navigation to store details from dashboard
  };

  const goHome = useCallback(() => {
    //setActiveTab(null);
    setSelectedService(null);
    setSelectedStore(null);
    setFilter("Unhealthy");
    setPageByFilter({ All: 1, Healthy: 1, Unhealthy: 1, Amber: 1 });
  }, []);

  const backToStoresDashboard = () => {
    setSelectedStore(null);
  };
  /* ------------ Render ------------- */
  const hasServiceData =
  Object.values(servicesDataDisplay || {}).length > 0;

  // Optional simple error banners (kept minimal to preserve design)
  const hasAnyError = storesError || servicesError;



  return (
    <div className="oh-dashboard">
      {/* TABS HEADER */}
      {!showHexagonDemo ? (
        <>
          {" "}
          <div className="oh-tabs">
            <button
              className={`oh-tab ${activeTab === TABS.HEALTH ? "active" : ""}`}
              onClick={() => setActiveTab(TABS.HEALTH)}
            >
              Operational Health
            </button>

            <button
              className={`oh-tab ${activeTab === TABS.AI ? "active" : ""}`}
              onClick={() => setActiveTab(TABS.AI)}
            >
              Operational Insights – AI Assistant
            </button>
          </div>
        </>
      ) : null}
      {/* TAB CONTENT */}

      <div className={activeTab === TABS.HEALTH ? "tab-visible" : "tab-hidden"}>
        {activeTab === TABS.HEALTH && (
          <>
            {showHexagonDemo ? (
              <>
                {storeCard ? (
                  // selectedStore ? (
                  //   <StoreStatusDetails
                  //     storeId={selectedStore}
                  //     onGoHome={goHome}
                  //     onBack={backToStoresDashboard}
                  //   />
                  // ) : (
                  <StoreStatusDashboard
                    source="stores"
                    onStoreClick={setSelectedStore}
                    onGoHome={onGoHomeFromDash}
                    onBack={handleBackFromHexagonDemo}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    stores={stores}
                    setStores={setStores}
                    filter={filter}
                    setFilter={setFilter}
                  />
                ) : (
                  // )
                  <>
                    <StoresDataFetcher
                      key={selectedService?.key || "no-service"}
                      onDataLoaded={setStores}
                      serviceKey={selectedService?.key}
                      enabled={Boolean(selectedService)}
                    />

                    <StoreStatusDashboard
                      source="services"
                      selectedService={selectedService}
                      onBack={handleBackFromHexagonDemo}
                      onGoHome={onGoHomeFromDash}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      stores={stores}
                      setStores={setStores}
                      filter={filter}
                      setFilter={setFilter}
                      onStoreClick={onStoreClick}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <div className="oh-page-header">
                  <h1 className="oh-page-title">Operational Health</h1>
                  <div className="oh-page-meta">
                  {relativeTime && (<div
  className="opsc-small"
  title={`Last updated at ${new Date(lastUpdatedTimestamp).toLocaleString()}`}
>
  Last updated: {relativeTime} • Status: <strong>Live</strong>
</div>)}
                  </div>
                </div>

                <div className="oh-top-card">
                  <button
                    className="oh-card__header"
                    onClick={() => setIsTrendOpen((v) => !v)}
                    aria-expanded={isTrendOpen}
                  >
                    Historical Trendline (All Stores){" "}
                    {/*isTrendOpen ? "Collapse" : "Expand" */}
                  </button>

                  {isTrendOpen && (
                    <div className="oh-card__body">
                      <DisplayLineChart />
                    </div>
                  )}
                </div>

                {/* <button
  className={`accordion-header ${isOpen ? "open" : ""}`}
  onClick={() => setIsOpen(v => !v)}
>
  <span>Historical Trendline (All Stores)</span>
  <span className="arrow"></span>
</button>

{isOpen && (
  <div className="accordion-body">
    <DisplayLineChart />
  </div>
)} */}

                {hasAnyError && (
                  <div
                    className="oh-card"
                    role="alert"
                    style={{ borderColor: "#f44336" }}
                  >
                    <div className="oh-card-title">Data Load Error</div>
                    <div style={{ color: "#b71c1c" }}>
                      {storesError?.message ||
                        servicesError?.message ||
                        "Unknown error"}
                    </div>
                  </div>
                )}

                <div className="oh-content">
                  {/* LEFT PANEL */}
                  <div className="oh-panel oh-left">
                    <div className="oh-panel-heading">
                      Service Roll-up (0–100% healthy)
                    </div>

                    {servicesLoading && (
                      <div className="oh-card">
                        <div className="oh-card-title">{UI.LOADING}</div>
                      </div>
                    )}

                    {!servicesLoading && !hasServiceData  && (
                      <div className="oh-card">
                        <div className="oh-card-title">No service data</div>
                        <div className="oh-small">
                          The service health API returned no metrics.
                        </div>
                      </div>
                    )}

                  {}
                    <>
                      {Object.values(servicesDataDisplay).map((service) => (
                        <OperationalServiceCard
                          key={service.name}
                          name={service.name}
                          percent={service.percent}
                          color={
                            service.percent >= 100
                              ? "oh-green"
                              : service.percent >= 80
                                ? "oh-yellow"
                                : "oh-red"
                          }
                          note={
                            service.impactedCount > 0
                              ? `${service.impactedCount} impacted store${
                                  service.impactedCount === 1 ? "" : "s"
                                }`
                              : `${service.name} - No Issues`
                          }
                          //digitalDetailGroups={service.digitalDetailGroups}
                           storesData={storesData}  
                          // Only the services with a page behind them get a
                          // handler; the card reads this to decide whether to
                          // offer a pointer. labelToServiceKey is the same
                          // lookup handleCardClick would fail on.
                          onClick={
                            labelToServiceKey(service.name)
                              ? () => handleCardClick(service)
                              : undefined
                          }

                        />
                      ))}
                    </>
                  </div>

                  {/* RIGHT PANEL */}
                  <div className="oh-panel oh-right">
                    <div className="oh-panel-heading">
                      Stores Impact &amp; Context
                    </div>

                    {/* OVERALL HEALTH */}
                    {/* <div className="oh-card" onClick={handleStoreCardClick} role="button" tabIndex={0}
                   onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleStoreCardClick(); }}>
                <div className="oh-card-title">Health</div>
                <ProgressBar
                  percent={healthPercent}
                  color={
                    healthPercent >= 80
                      ? "oh-green"
                      : healthPercent >= 60
                      ? "oh-yellow"
                      : "oh-red"
                  }
                />
                <div className="oh-percent-text">{healthPercent}%</div>
              </div> */}

                    <div
                      className="oh-card clickable"
                      onClick={handleStoreCardClick}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          handleStoreCardClick();
                      }}
                      aria-label={`Health ${healthPercent}%`}
                    >
                      <div className="oh-card-header">
                        <div className="oh-card-title">Health</div>
                        <div className="oh-percent-text">{healthPercent}%</div>
                      </div>

                       <OperationalProgressBar
                        percent={healthPercent}
                        color={
                          healthPercent >= 99
                            ? "oh-green"
                            : healthPercent >= 80
                              ? "oh-yellow"
                              : "oh-red"
                        }
                      />
                    </div>

                    {/* IMPACT SUMMARY */}
                    <div className="oh-card">
                      <div className="oh-card-title">Impact Summary</div>
                      <p>
                        Stores with any issue:{" "}
                        <strong>
                          {impactedStoresCount} / {allStoresCount} (
                          {impactedPercent}%)
                        </strong>
                      </p>
                    </div>

                    {/* IMPACTED STORES LIST */}
                  <OperationalRightPanelCard
      impactedStoresCount={impactedStoresCount}
      allStoresCount={allStoresCount}
      activeTab={activeAlertTab}
      setActiveTab={setActiveAlertTab}
      storesData={storesData}
      alertsDataDisplay={alertsDataDisplay}
      isImpactedColor={isImpactedColor}
      openEntityWithOneDayDuration={openEntityWithOneDayDuration}
    />
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className={activeTab === TABS.AI ? "tab-visible" : "tab-hidden"}>
        <OperationalInsights />
      </div>
    </div>
  );
}

/* ---------- Reusable Components ---------- */
function ServiceCard_nonprod({
  name,
  percent,
  color,
  note,
  issues = [], digitalDetailGroups = {}, 
  onClick,
   accountId,
}) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const visibleIssues = expanded ? issues : issues.slice(0, 2);
 //console.log("Rendering ServiceCard:", { name });
 const storeVar = name === "DigitalOrdering1" ? "store" : "entity";
 //const entityGuid = ENTITY_GUIDS?.[name];
  return (
    <div className="oh-card oh-service-card clickable"
 onClick={(e) => {
        if (e.target === e.currentTarget) onClick?.(e);
      }}
 //onClick={onClick}
 >
      <div className="oh-service-header">
        <span>{name}</span>
        <span>{percent}%</span>
      </div>

      <ProgressBar percent={percent} color={color} />

      <small className="oh-small">{note}</small>
    { /* console.log("ServiceCard issues:", { digitalDetailGroups }) */}
{ 

 Object.keys(digitalDetailGroups).length > 0 ? (
    <>
      {Object.values(digitalDetailGroups).map((grp) => (
        <Collapsible
          key={grp.alert}
          title={`${grp.alert} (${grp.count})`}
        onClick={''}>
          {grp.stores
          .filter((item) => item?.alert != null && String(item.alert).trim() !== '')
          .sort((a, b) => Number(a.store) - Number(b.store))
          .map((item, index) => ( 
            <div
              key={`${item.store}-${index}`}
              className="issue-row"
              onClick={(e) => {
                
                e.stopPropagation();
				const accKey = String(accountId).trim();   // "4091918"
				const svcKey = String(name).trim();        // "Sesame"

  // 2) All GUIDs in one place, keyed by accountId
  const ENTITY_GUIDS_BY_ACCOUNT = {
    default: {
      
      DigitalOrdering: "MzU5MjU1NnxWSVp8REFTSEJPQVJEfGRhOjEyMjY0OTYw",
	  Sesame: "MzU5MjU1NnxWSVp8REFTSEJPQVJEfGRhOjEyMjEyOTc2",
      eProd: "vvvv",
      MyStore: "MzU5MjU1NnxWSVp8REFTSEJPQVJEfGRhOjEyMjEyOTc5",
    },
    "4091918": {
      Sesame: "NDA5MTkxOHxWSVp8REFTSEJPQVJEfGRhOjEyMjEyODUw",
      DigitalOrdering: "NDA5MTkxOHxWSVp8REFTSEJPQVJEfGRhOjEyMjEyOTUy",
      eProd: "vvvv",
      MyStore: "NDA5MTkxOHxWSVp8REFTSEJPQVJEfGRhOjEyMjEyODcz",
    },
    // Add more accounts here as needed...
  };

  // 3) Pick the correct map for the account, else fallback to default
  const guidMap =
    ENTITY_GUIDS_BY_ACCOUNT[accKey] || ENTITY_GUIDS_BY_ACCOUNT.default;

  // 4) Look up the GUID for the selected service name
  //const entityGuid = guidMap[svcKey];
  console.log("Issue row clicked:", item);
                 console.log("Item GUID:", item?.guid ?? "NO_GUID");
                 const entityGuid = item?.guid ?? guidMap[svcKey] ?? "";
              if (!entityGuid) {
                console.warn(`No ENTITY_GUID found for name="${name}"`);
                return;
              }
                {loading && <Spinner />}
                openEntityWithOneDayDuration(entityGuid, item.store)
                 .finally(() => setLoading(false));
              }}
            >
              <strong>{item?.store ?? ''}</strong> — {item?.detail ?? ''} 
            </div>
          ))}
        </Collapsible>
      ))}
    </>
  ) :''}
  
    </div>
  );
}

 function ServiceCard({
  name,
  percent,
  color,
  note,
  digitalDetailGroups = {},
  onClick
}) {
  const [loading, setLoading] = useState(false);
  const [parentOpen, setParentOpen] = useState(false);
const [childOpenStates, setChildOpenStates] = useState({});
  const groupKeys = Object.keys(digitalDetailGroups);

  const handleRowClick = useCallback((entityGuid, storeId, time) => {
    console.time("handleRowClick");
    console.log("Issue row clicked - navigating to entity:", { entityGuid, storeId, time });
     
    navigation.openStackedEntity(entityGuid);
    console.timeEnd("handleRowClick");
    //openEntityWithOneDayDuration(entityGuid, storeId);
}, []);
useEffect(() => {
  const init = {};
  groupKeys.forEach((key) => (init[key] = false));
  setChildOpenStates(init);
}, [groupKeys.length]);

const toggleParent = (isOpen) => {
  setParentOpen(isOpen);

  if (!isOpen) {
    //  Collapse all children when parent closes
    const collapsed = {};
    groupKeys.forEach((key) => (collapsed[key] = false));
    setChildOpenStates(collapsed);
  }
};
//console.log("Rendering ServiceCard:", { name, groupKeys }); 
  return (
    <div
      className="oh-card oh-service-card clickable"
      onClick={(e) => {
        if (
          e.target.closest(".servicecard-collapse-container") ||
          e.target.closest(".issue-row")
        ) return;

        onClick?.();
      }}
    >
      {/* Header */}
      <div className="oh-service-header">
        <span>{name}</span>
        <span>{percent}%</span>
      </div>

      <ProgressBar percent={percent} color={color} />

      {/* {name !== "MyStore" && (
        <small className="oh-small">{note}</small>
      )} */}
{console.log("ServiceCard issues:", { digitalDetailGroups })}
      {/* 🟦 Parent Collapsible */}
      {groupKeys.length > 0 ? (
        <ServiceCardCollapse
  title={name !== "MyStore" ? ` ${note}` : name}
  isParent={true}
  open={parentOpen}
   setOpen={toggleParent}
  //setOpen={setParentOpen}
>
  {Object.values(digitalDetailGroups).map((grp) => (
    <ServiceCardCollapse
      key={grp.alert}
      title={`${grp.alert} (${grp.count})`}
      
 open={childOpenStates[grp.alert]}
  setOpen={(val) =>
    setChildOpenStates((prev) => ({ ...prev, [grp.alert]: val }))
  }

    >
      {console.log("Rendering group:", grp.alert, "with stores:", grp.stores)}
      {grp.stores.map((item, index,i=0) => {
        const entityGuid = (item?.guid ?? ENTITY_GUIDS?.[name]) || null;
console.log('entityid',entityGuid,i++)
        return (
          <div
            className="issue-row"
            key={`${item.store}-${index}`}
            onClick={() => handleRowClick(entityGuid, item.store, performance.now())}
          >
            {item.store} — {item.detail}
          </div>
        );
      })}
    </ServiceCardCollapse>
  ))}
</ServiceCardCollapse>

      ) : (
  <small className="oh-small">{note}</small>
)}



      {loading && <Spinner />}
    </div>
  );
}



function ServiceCardCollapse({ title, children, isParent = false, open, setOpen }) {

  const toggle = (e) => {
    e.stopPropagation();
    setOpen(!open);
  };

  return (
    <div className={`servicecard-collapse-container ${isParent ? "parent" : "child"}`}>
      <button
        type="button"
        className={`servicecard-collapse-header ${open ? "active" : ""}`}
        onClick={toggle}
      >
        <span className="servicecard-collapse-title">{title}</span>
        <span className="servicecard-collapse-arrow">▼</span>
      </button>

      <div className={`servicecard-collapse-content ${open ? "open" : ""} ${isParent ? "parent-content" : "child-content"}`}>
        {children}
      </div>
    </div>
  );
}

// function ProgressBar({ percent, color }) {
//   return (
//     <div
//       className="oh-progress-bar"
//       role="progressbar"
//       aria-valuenow={percent}
//       aria-valuemin={0}
//       aria-valuemax={100}
//     >
//       <div className={`oh-progress-fill ${color}`} style={{ width: `${percent}%` }} />
//     </div>
//   );
// }
// function openEntityWithOneDayDuration(entityGuid, storeId, accountId) {
//   const oneDay = 1 * 24 * 60 * 60 * 1000;

//   nerdlet.setConfig({
//     timePickerDefaultOffset: oneDay,
//   });

//   const accId = Number(accountId); // Make sure it's a number


// //console.log("Entity GUID:", entityGuid);
//   // navigation.openStackedNerdlet({
//   //   id: "dashboards.detail",
//   //   //accountId: accId,
//   //   urlState: {
//   //     entityGuid: entityGuid, //"MzU0NzU3M3xWSVp8REFTSEJPQVJEfGRhOjEyMTc2ODIz",
//   //     selectedVariables: {
//   //       entity: storeId,
//   //     },
//   //   },
//   // });
// navigation.openStackedEntity(entityGuid.trim(), {
//   // selectedVariables: {
//   //   [storeVariable]: storeId
//   // }
// });

// }
function ProgressBar({ percent, color }) {
  return (
    <div
      className="oh-progress-bar"
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Health progress ${percent}%`}
    >
      <div
        className={`oh-progress-fill ${color}`}
        style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
      />
    </div>
  );
}
